import { fetchWithCSRF } from "@/helpers/common/csrf";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    Hammer,
    Search,
} from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const endpoint = `${API_URL}/longhouse-api/village/exalted-reforge`;

interface ReforgeEntry {
    player_id: string;
    player_name: string;
    exalted_level: number;
    earnt: number;
    spent: number;
    remaining: number;
}

interface Pagination {
    page: number;
    total_items: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
}

const controlClass =
    "rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";

export default function ExaltedReforgePage() {
    const [entries, setEntries] = useState<ReforgeEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [filters, setFilters] = useState({
        search: "",
        balance: "available",
        sortBy: "remaining",
        order: "desc",
        page: 1,
    });
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total_items: 0,
        total_pages: 1,
        has_next_page: false,
        has_previous_page: false,
    });
    const [refreshCount, setRefreshCount] = useState(0);
    const [selected, setSelected] = useState<ReforgeEntry | null>(null);
    const [amount, setAmount] = useState("1");
    const [spending, setSpending] = useState(false);
    const [spendError, setSpendError] = useState("");

    useEffect(() => {
        const controller = new AbortController();
        const load = async () => {
            setLoading(true);
            setError("");
            const params = new URLSearchParams({
                page: String(filters.page),
                page_size: "25",
                search: filters.search,
                balance: filters.balance,
                sort_by: filters.sortBy,
                order: filters.order,
            });
            try {
                const response = await fetchWithCSRF(`${endpoint}/?${params}`, {
                    signal: controller.signal,
                });
                if (!response.ok)
                    throw new Error("Unable to load reforge balances.");
                const data = await response.json();
                if (!controller.signal.aborted) {
                    setEntries(data.results);
                    setPagination(data.pagination);
                }
            } catch (failure) {
                if (!controller.signal.aborted) {
                    setError(
                        failure instanceof Error
                            ? failure.message
                            : "Unable to load reforge balances.",
                    );
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        void load();
        return () => controller.abort();
    }, [filters, refreshCount]);

    const quantity = Number(amount);
    const validAmount =
        selected !== null &&
        Number.isInteger(quantity) &&
        quantity > 0 &&
        quantity <= selected.remaining;

    const handleSpend = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selected || !validAmount || spending) return;
        setSpending(true);
        setSpendError("");
        try {
            const response = await fetchWithCSRF(
                `${endpoint}/${selected.player_id}/spend`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ amount: quantity }),
                },
            );
            const data = await response.json();
            if (!response.ok)
                throw new Error(
                    data.detail || "Unable to spend Reforge Tokens.",
                );
            setSuccess(
                `Spent ${quantity} Reforge ${quantity === 1 ? "Token" : "Tokens"} for ${selected.player_name}. Remaining: ${data.remaining}.`,
            );
            setSelected(null);
            setRefreshCount((count) => count + 1);
        } catch (failure) {
            setSpendError(
                failure instanceof Error
                    ? failure.message
                    : "Unable to spend Reforge Tokens.",
            );
        } finally {
            setSpending(false);
        }
    };

    return (
        <div className="mx-auto mt-4 w-full px-4 pb-6">
            <h1 className="mb-6 text-2xl font-bold text-slate-900 dark:text-slate-100">
                Exalted Reforge
            </h1>
            <div className="mb-6 flex flex-wrap items-center gap-4">
                <div className="relative w-full min-w-0 sm:max-w-xs sm:flex-1">
                    <Search className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                        aria-label="Search players"
                        placeholder="Search by player name..."
                        value={filters.search}
                        onChange={(event) =>
                            setFilters({
                                ...filters,
                                search: event.target.value,
                                page: 1,
                            })
                        }
                        className={`${controlClass} w-full pl-10`}
                    />
                </div>
                <select
                    aria-label="Filter token balance"
                    value={filters.balance}
                    onChange={(event) =>
                        setFilters({
                            ...filters,
                            balance: event.target.value,
                            page: 1,
                        })
                    }
                    className={controlClass}
                >
                    <option value="all">All balances</option>
                    <option value="available">Tokens available</option>
                    <option value="empty">No tokens remaining</option>
                </select>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                    {pagination.total_items} players
                </span>
            </div>
            {success && (
                <p
                    role="status"
                    className="mb-4 text-sm text-emerald-700 dark:text-emerald-400"
                >
                    {success}
                </p>
            )}
            {error && (
                <div
                    role="alert"
                    className="mb-4 flex flex-wrap items-center gap-3 text-sm text-red-700 dark:text-red-400"
                >
                    {error}
                    <button
                        onClick={() => setRefreshCount((count) => count + 1)}
                        className="underline"
                    >
                        Retry
                    </button>
                </div>
            )}
            <div className="overflow-hidden rounded-lg border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            <tr>
                                {[
                                    { label: "Player", field: "player_name" },
                                    {
                                        label: "Exalted Level",
                                        field: "exalted_level",
                                    },
                                    { label: "Earnt", field: "earnt" },
                                    { label: "Spent", field: "spent" },
                                    { label: "Remaining", field: "remaining" },
                                    { label: "Actions", field: null },
                                ].map(({ label, field }) => (
                                    <th
                                        key={label}
                                        scope="col"
                                        aria-sort={
                                            field === null
                                                ? undefined
                                                : filters.sortBy === field
                                                  ? filters.order === "asc"
                                                      ? "ascending"
                                                      : "descending"
                                                  : "none"
                                        }
                                        className={`px-6 py-3 font-semibold whitespace-nowrap ${field === "player_name" ? "text-left" : "text-right"}`}
                                    >
                                        {field === null ? (
                                            label
                                        ) : (
                                            <button
                                                type="button"
                                                title={`Sort by ${label.toLowerCase()}`}
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        sortBy: field,
                                                        order:
                                                            filters.sortBy ===
                                                            field
                                                                ? filters.order ===
                                                                  "desc"
                                                                    ? "asc"
                                                                    : "desc"
                                                                : field ===
                                                                    "player_name"
                                                                  ? "asc"
                                                                  : "desc",
                                                        page: 1,
                                                    })
                                                }
                                                className={`inline-flex w-full items-center gap-2 rounded focus-visible:ring-2 focus-visible:ring-blue-500 ${field === "player_name" ? "justify-start" : "justify-end"}`}
                                            >
                                                {label}
                                                {filters.sortBy !== field ? (
                                                    <ArrowUpDown
                                                        aria-hidden="true"
                                                        className="h-4 w-4 shrink-0 text-slate-400"
                                                    />
                                                ) : filters.order === "desc" ? (
                                                    <ArrowDown
                                                        aria-hidden="true"
                                                        className="h-4 w-4 shrink-0"
                                                    />
                                                ) : (
                                                    <ArrowUp
                                                        aria-hidden="true"
                                                        className="h-4 w-4 shrink-0"
                                                    />
                                                )}
                                            </button>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-12 text-center text-slate-500"
                                        role="status"
                                    >
                                        Loading...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-12 text-center text-slate-500"
                                    >
                                        Balances unavailable
                                    </td>
                                </tr>
                            ) : entries.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
                                    >
                                        No players found
                                    </td>
                                </tr>
                            ) : (
                                entries.map((entry) => (
                                    <tr
                                        key={entry.player_id}
                                        className="text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-6 py-4 font-medium break-words text-slate-900 dark:text-slate-100">
                                            {entry.player_name}
                                        </td>
                                        {[
                                            entry.exalted_level,
                                            entry.earnt,
                                            entry.spent,
                                            entry.remaining,
                                        ].map((value, index) => (
                                            <td
                                                key={index}
                                                className="px-6 py-4 text-right tabular-nums"
                                            >
                                                {value}
                                            </td>
                                        ))}
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                disabled={entry.remaining <= 0}
                                                onClick={() => {
                                                    setSelected(entry);
                                                    setAmount("1");
                                                    setSpendError("");
                                                    setSuccess("");
                                                }}
                                                aria-label={`Spend tokens for ${entry.player_name}`}
                                                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 font-medium whitespace-nowrap text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400 dark:disabled:bg-slate-700"
                                            >
                                                <Hammer className="h-4 w-4" />
                                                Spend tokens
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                        Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            title="Previous page"
                            aria-label="Previous page"
                            disabled={loading || !pagination.has_previous_page}
                            onClick={() =>
                                setFilters({
                                    ...filters,
                                    page: pagination.page - 1,
                                })
                            }
                            className={`${controlClass} disabled:opacity-40`}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            title="Next page"
                            aria-label="Next page"
                            disabled={loading || !pagination.has_next_page}
                            onClick={() =>
                                setFilters({
                                    ...filters,
                                    page: pagination.page + 1,
                                })
                            }
                            className={`${controlClass} disabled:opacity-40`}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
            <Dialog
                open={selected !== null}
                onClose={() => {
                    if (!spending) setSelected(null);
                }}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
                    <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 text-slate-900 shadow-xl dark:bg-slate-900 dark:text-slate-100">
                        <DialogTitle className="text-lg font-semibold">
                            Spend Reforge Tokens
                        </DialogTitle>
                        <p className="mt-2 text-sm break-words">
                            {selected?.player_name}
                        </p>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                            Available: {selected?.remaining}
                        </p>
                        <form onSubmit={handleSpend}>
                            <label
                                htmlFor="reforge-amount"
                                className="mt-5 block text-sm font-medium"
                            >
                                Tokens to spend
                            </label>
                            <input
                                id="reforge-amount"
                                type="number"
                                min={1}
                                max={selected?.remaining}
                                step={1}
                                required
                                disabled={spending}
                                value={amount}
                                onChange={(event) =>
                                    setAmount(event.target.value)
                                }
                                className={`${controlClass} mt-2 w-full`}
                            />
                            {validAmount && (
                                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                    Remaining after spend:{" "}
                                    {(selected?.remaining ?? 0) - quantity}
                                </p>
                            )}
                            {spendError && (
                                <p
                                    role="alert"
                                    className="mt-3 text-sm text-red-600 dark:text-red-400"
                                >
                                    {spendError}
                                </p>
                            )}
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    disabled={spending}
                                    onClick={() => setSelected(null)}
                                    className={controlClass}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!validAmount || spending}
                                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <Hammer className="h-4 w-4" />
                                    {spending ? "Spending..." : "Confirm spend"}
                                </button>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>
        </div>
    );
}
